import { AxiosReadPayload } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-payload';
import { axiosReadHelper } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-helper';
import { AxiosResultStatus } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-result-status';

export type AvailityVobOverrides = {
  fname?: string;
  lname?: string;
  dob?: string;
  memberId?: string;
  pcode?: string;
  npi?: string;
};

export type FacilityWithNpi = {
  facilityId: number;
  npi: string;
  facilityName?: string;
};

/** Inline menu when fewer than this many NPI facilities; at this count use modal. */
export const AVAILITY_FACILITY_MENU_MAX = 10;

/** Default outer-retry window when env is unset/invalid. */
export const DEFAULT_AVAILITY_VOB_PROCESSING_RETRY_MINUTES = 2;

/** Wait between outer retries while Availity reports still processing. */
export const AVAILITY_VOB_PROCESSING_RETRY_DELAY_MS = 5_000;

type FacilityListItem = {
  facilityId: number;
  facilityName?: string;
  npi?: string | null;
};

/**
 * Facilities (list order) that already include a non-empty NPI.
 * Client list payloads include npi — do not GET each facility.
 */
export function listFacilitiesWithValidNpi(
  facilities: FacilityListItem[] | undefined | null
): FacilityWithNpi[] {
  if (!facilities?.length) {
    return [];
  }
  const withNpi: FacilityWithNpi[] = [];
  for (const facility of facilities) {
    const facilityId = Number(facility.facilityId);
    if (!Number.isInteger(facilityId) || facilityId <= 0) {
      continue;
    }
    const npi = String(facility.npi ?? '').trim();
    if (!npi) {
      continue;
    }
    const facilityName = (facility.facilityName || '').trim() || undefined;
    withNpi.push({ facilityId, npi, facilityName });
  }
  return withNpi;
}

/** Minutes to keep outer-retrying while Availity reports still processing. */
export function getAvailityVobProcessingRetryMinutes(): number {
  const raw = process.env.REACT_APP_AVAILITY_VOB_PROCESSING_RETRY_MINUTES;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return DEFAULT_AVAILITY_VOB_PROCESSING_RETRY_MINUTES;
  }
  return n;
}

/** Loader copy for the Availity wait budget (singular/plural). */
export function formatAvailityLoaderTimeWarn(minutes: number): string {
  const n = Number.isFinite(minutes) && minutes > 0 ? Math.floor(minutes) : 1;
  const unit = n === 1 ? 'minute' : 'minutes';
  return `This can take up to ${n} ${unit}.`;
}

/** True when the Availity /vob error body is the in-process / try-later response. */
export function isAvailityVobProcessingResponse(
  response: AxiosResultStatus | null | undefined
): boolean {
  const message = String(response?.entity?.message ?? '');
  return /still in process/i.test(message);
}

function coverageIdFromResponse(
  response: AxiosResultStatus | null | undefined
): string | undefined {
  const id = response?.entity?.coverageId;
  if (typeof id !== 'string') {
    return undefined;
  }
  const trimmed = id.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

async function fetchAvailityVobOnce(
  patientId: number,
  facilityId: number,
  overrides: AvailityVobOverrides | undefined,
  coverageId: string | undefined,
  signal?: AbortSignal
): Promise<AxiosResultStatus> {
  const params = new URLSearchParams({
    patientId: String(patientId),
    facilityId: String(facilityId),
    retryCount: '1',
  });
  if (overrides) {
    for (const key of ['fname', 'lname', 'dob', 'memberId', 'pcode', 'npi'] as const) {
      const value = overrides[key]?.trim();
      if (value) {
        params.set(key, value);
      }
    }
  }
  if (coverageId) {
    params.set('coverageId', coverageId);
  }
  const payload: AxiosReadPayload = {
    dataId: -2,
    url: `availity/vob?${params.toString()}`,
    ...(signal ? { signal } : {}),
  };
  return axiosReadHelper(payload);
}

/** Resolves after `ms`, or sooner if `signal` aborts. */
export function delayAvailityVobRetry(
  ms: number,
  signal?: AbortSignal
): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * GET availity/vob with optional demographics / NPI query overrides.
 * Always sends retryCount=1. When Availity returns still-processing with a
 * coverageId, waits AVAILITY_VOB_PROCESSING_RETRY_DELAY_MS then re-calls with
 * that id until success, a non-processing error, abort, or the
 * REACT_APP_AVAILITY_VOB_PROCESSING_RETRY_MINUTES budget elapses.
 */
export async function getAvailityVobWithOverrides(
  patientId: number,
  facilityId: number,
  overrides?: AvailityVobOverrides,
  signal?: AbortSignal
): Promise<AxiosResultStatus> {
  const deadlineMs =
    Date.now() + getAvailityVobProcessingRetryMinutes() * 60_000;
  let coverageId: string | undefined;

  while (true) {
    if (signal?.aborted) {
      const aborted = new AxiosResultStatus();
      aborted.hasErrors = true;
      aborted.errorMessage = 'canceled';
      return aborted;
    }

    const response = await fetchAvailityVobOnce(
      patientId,
      facilityId,
      overrides,
      coverageId,
      signal
    );

    if (isAvailityRequestCanceled(response, signal)) {
      return response;
    }

    if (!response.hasErrors) {
      return response;
    }

    if (!isAvailityVobProcessingResponse(response)) {
      return response;
    }

    const nextCoverageId = coverageIdFromResponse(response);
    if (!nextCoverageId) {
      return response;
    }

    if (Date.now() >= deadlineMs) {
      return response;
    }

    coverageId = nextCoverageId;
    await delayAvailityVobRetry(AVAILITY_VOB_PROCESSING_RETRY_DELAY_MS, signal);
  }
}

/** True when the Availity helper result is an aborted/canceled request. */
export function isAvailityRequestCanceled(
  response: AxiosResultStatus | null | undefined,
  signal?: AbortSignal
): boolean {
  if (signal?.aborted) {
    return true;
  }
  const message = String(response?.errorMessage ?? '').toLowerCase();
  return (
    message.includes('canceled') ||
    message.includes('cancelled') ||
    message.includes('aborted')
  );
}

export function formatAvailityDob(raw: string | null | undefined): string {
  if (!raw) {
    return '';
  }
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function facilityDisplayName(facility: FacilityWithNpi): string {
  return (facility.facilityName || '').trim() || `Facility ${facility.facilityId}`;
}
